import { Currency } from "./common";

export const PROP_NAME_SPLITER = ':';

export enum FlightSearchProvider {
  TRIPENGER = 'tripenger',
  TRIP_DOT_COM = 'trip_dot_com',
  TRAVELPAYOUTS = 'travelpayouts',
}

export enum PassengerType {
  NONE = '',
  ADULT = 'ADULT',
  CHILD = 'CHILD',
  INFANT = 'INFANT',
  HELD_INFANT = 'HELD_INFANT',
}

export enum UnitOption {
  QUANTITY = 'quantity',
  KG = 'KG',
  LB = 'LB',
  CM = 'CM',
}

export enum AdditonalServiceType {
  CHARGEABLE_CHECKED_BAGS = 'CHARGEABLE_CHECKED_BAGS',
}

export interface AgentFees {
  fees: Array<Fee>,
  total: string,
}
export interface FlightOfferPriceCheckResponse {
  bookingContext: string,
  priceCheckId: string;
  dateCreated: string;
  response: Response;
  agentFees: AgentFees;
  agentFeesOnly: boolean;
}

export interface BagsByLeg {
  [key: string]: Record<string, BaggageItem>;
}

export interface BasicOption {
  type: string,
  description: string,
}

export interface Response {
  source: string;
  requestId: string;
  offers: Offer[];
  bookingRequirements: BookingRequirements;
  upsellExtras?: UpsellExtras;
  fareRules?: Record<string, FareRule>;
}

export interface Offer {
  searchId?: string;
  offerId: string;
  flightType: string;
  lastTicketDate: string;
  lastTicketDateTime: string;
  itineraries: Itinerary;
  price: Price;
  passengerPricing: PassengerPricing[];
  seatsAvailable: string;
  agentFees: AgentFees;
}

export interface Itinerary {
  outbound: LegGroup;
  inbound: LegGroup;
}

export interface LegGroup {
  duration: string;
  legs: Leg[];
}

export interface Leg {
  departure: LocationDetails;
  arrival: LocationDetails;
  carrier: Carrier;
  flightNumber: string;
  aircraft: Aircraft;
  operatedBy: Carrier;
  numberOfStops: number;
  duration: string;
  legId: string;
  co2Emissions: Co2Emission[];
}

export interface LocationDetails {
  location: CodeAndName;
  country: CodeAndName;
  city: CodeAndName;
  terminal: string;
  dateTime: string;
}

export interface CodeAndName {
  code: string;
  friendlyName: string;
}

export interface Carrier {
  code: string;
  friendlyName: string;
}

export interface Aircraft {
  code: string;
  friendlyName: string;
}

export interface Co2Emission {
  value: number;
  unit: string;
  cabin: string;
}

export interface Price {
  currency: Currency;
  billingCurrency: string;
  total: string;
  base: string;
  fees: Fee[];
  grandTotal: string;
  includedCheckedBagsOnly: boolean;
  validatingAirlines: Airline[];
  fareType: string[];
}

export interface Fee {
  amount: string,
  type?: string,
  code?: string,
  currency?: Currency,
}

export interface Airline {
  code: string;
  friendlyName: string;
}

export interface PassengerPricing {
  id: string;
  associatedAdultId?: string;
  passengerType: PassengerType;
  fareOptions: string;
  price: PassengerPrice;
  fareDetailsByTripLeg: FareDetail[];
}

export interface PassengerPrice {
  currency: Currency;
  total: string;
  base: string;
  refundableTaxes: string;
  taxes: Tax[];
}

export interface BagSelection {
  passengerId: string;
  legIds: string[];
  bagNumbers: string[];
  weight?: BaggageItem;
  quantity?: string;
  retailPrice: { amount: string; currency: Currency };
  label: string;
  legs?: Leg[][];
}

export interface ExtraBags {
  available: Bag[];
  inCart: Bag[];
}

export interface ExtraBagsByLegs {
  [key: string]: ExtraBags;
}

export interface Tax {
  amount: string;
  code: string;
}

export interface OffersSummary {
  minPrice?: number;
  maxPrice?: number;
  carriers?: string[];
}

export interface BaggageItem {
  value: string,
  unit: UnitOption,
  cabin?: string
}

export interface AdditionalService {
  type: AdditonalServiceType;
  value: string;
  unit: UnitOption;
}

export interface FareDetail {
  legId: string;
  cabin: string;
  fareBases: string;
  brandedFare: string;
  class: string;
  includedCheckedBags: BaggageItem;
  additionalServices: AdditionalService[];
}

export interface BookingRequirements {
  emailAddressRequired: boolean;
  mobilePhoneNumberRequired: boolean;
  passengerRequirements: PassengerRequirement[];
}
export interface PassengerRequirement {
  passengerId: string;
  documentRequired: boolean;
}

export interface Passenger {
  id: string;
  firstName: string;
  lastName: string;
  gender?: string;
  contactDetails?: ContactDetails;
  dateOfBirth?: string;
  documents?: Document[] | null;
  companyName?: string;
  passengerType?: PassengerType;
  documentsRequired?: boolean;
  [key: string]: any;
  extraBaggage?: ExtraBagsByLegs;
}

export interface ContactDetails {
  addressLines?: string[];
  postalCode?: string;
  city?: CodeAndName;
  country?: CodeAndName;
  phoneNumbers: PhoneNumber[];
  email: string;
  [key: string]: any;
}

export interface PhoneNumber {
  countryCode: string;
  type: string;
  number: string;
  [key: string]: any;
}

export interface Document {
  documentType: string;
  birthPlace?: string;
  issuanceLocation?: string;
  issuanceDate?: string;
  number: string;
  expiryDate?: string;
  countryOfIssue?: CodeAndName;
  countryWhereValid?: CodeAndName;
  nationality: CodeAndName;
  holder: boolean;
}

export interface Remarks {
  general: GeneralRemark[];
}

export interface GeneralRemark {
  type: string;
  description: string;
}

export interface TicketingAgreement {
  option: string;
  delay: string;
}
export interface Bag {
  bagNumber: string,
  legIds: string[],
  passengerIds: string[],
  quantity?: string,
  weight?: BaggageItem,
  name: string,
  price: Fee,
  retailPrice: Fee,
  bookableByItinerary: boolean,
  selected: boolean,
  // The next fields are temp fields until there is clear way of handling different baggage types per leggroup  
  canBeSold: boolean,
  belongsToMasterLegGroup: boolean,
}
export interface UpsellExtras {
  bags?: Bag[],
}

export interface Order {
  offers: Offer[],
}
export interface FlightBookingPostParams {
  priceCheckId: string,
  remarks: Remarks | null,
  ticketingAgreement: TicketingAgreement | null,
  tripContactPersons: Passenger[] | null,
  passengers: Passenger[] | null,
  order: Order | null,
  source: string,
  termsAccepted: boolean,
}

export interface ListTypeMessage {
  index?: number,
  fields: Record<any, string[]>,
  children?: ListTypeMessage[] | null,
}

export interface FlightBookingPostParamsValidationMessage {
  tripContactPersons: ListTypeMessage[] | null,
  passengers: ListTypeMessage[] | null,
  globalErrorMessages: string[],
  termsAccepted: string[],
}

export interface FareRuleFee {
  fee: string | number;
  noShowFee: string | number;
}

export interface FareRuleDescription {
  cancellations: FareRuleFee;
  changes: FareRuleFee;
  failToBoardFee: string;
}

export interface FareRule {
  fareBasis: string;
  name: string;
  descriptions: FareRuleDescription;
  transFormedText?: BasicOption[];
  legId: string;
}